/**
 * MatrixToolbar Component
 * Top navigation bar with breadcrumb, search, sort, and view toggle
 */

import { useState, useEffect, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  Grid3X3,
  Award,
  Users,
  MapPin,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  ChevronDown,
  ChevronRight,
  FileUp
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Editable Matrix Name sub-component
const EditableMatrixName = memo(({ initialName, spaceName, projectId, onNameChange, isStackView, stackId, stackName }) => {
  const [matrixName, setMatrixName] = useState(initialName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(initialName);
  const navigate = useNavigate();

  useEffect(() => {
    setMatrixName(initialName);
    setTempName(initialName);
  }, [initialName]);

  const handleSave = async (newName) => {
    const trimmedName = newName.trim() || matrixName;

    if (trimmedName === matrixName) {
      setIsEditingName(false);
      return;
    }

    setMatrixName(trimmedName);
    setIsEditingName(false);

    try {
      const { error } = await supabase
        .from('projects')
        .update({ matrix_name: trimmedName })
        .eq('id', projectId);

      if (error) throw error;
      onNameChange?.(trimmedName);
      toast.success('Matrix name updated');
    } catch (error) {
      console.error('Error updating matrix name:', error);
      toast.error('Failed to update name');
      setMatrixName(initialName);
    }
  };

  return (
    <div className="flex items-center gap-2 text-sm">
      {isStackView ? (
        <>
          <button
            onClick={() => navigate(`/stack/${stackId}`)}
            className="text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            {stackName || 'Stack'}
          </button>
          <ChevronRight className="h-4 w-4 text-gray-400" />
          <span className="font-semibold text-gray-900">Matrix</span>
        </>
      ) : (
        <>
          <button
            onClick={() => navigate(`/space/${projectId}`)}
            className="text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            {spaceName}
          </button>
          <ChevronRight className="h-4 w-4 text-gray-400" />
          {isEditingName ? (
            <input
              type="text"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              onBlur={() => handleSave(tempName)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave(tempName);
                } else if (e.key === 'Escape') {
                  setTempName(matrixName);
                  setIsEditingName(false);
                }
              }}
              autoFocus
              className="font-semibold text-gray-900 bg-transparent border-b border-blue-500 outline-none px-0 py-0"
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              className="font-semibold text-gray-900 hover:text-blue-600 transition-colors"
            >
              {matrixName}
            </button>
          )}
        </>
      )}
    </div>
  );
});

EditableMatrixName.displayName = 'EditableMatrixName';

const MatrixToolbar = memo(({
  // Navigation
  projectId,
  spaceName,
  spaceTools = [],
  isStackView = false,
  stackId,
  stackName,
  stackProjects = [],
  selectedSpaceFilter,
  setSelectedSpaceFilter,

  // Matrix name
  matrixName,
  setMatrixName,

  // Search
  searchTerm,
  setSearchTerm,

  // View mode
  viewMode,
  setViewMode,

  // Sort
  sortBy,
  setSortBy,

  // User
  navigate
}) => {
  return (
    <div className="w-full bg-white border-b border-gray-200">
      <div className="flex items-center justify-between py-3 px-6">
        {/* Left side - Tool switcher + Breadcrumb + Space Filter (for stacks) */}
        <div className="flex items-center gap-3">
          {/* Space Filter Dropdown - Only show for stack view */}
          {isStackView && stackProjects && stackProjects.length > 0 && (
            <Select value={selectedSpaceFilter} onValueChange={setSelectedSpaceFilter}>
              <SelectTrigger className="w-[200px] h-8 text-sm">
                <SelectValue placeholder="Filter by space" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Spaces ({stackProjects.length})</SelectItem>
                {stackProjects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Tools Dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 flex items-center gap-1 border border-gray-200 bg-background hover:bg-muted data-[state=open]:bg-muted data-[state=open]:border-gray-300 rounded-md"
              >
                <LayoutGrid className="h-4 w-4 text-gray-600" />
                <ChevronDown className="h-3 w-3 text-gray-500 data-[state=open]:rotate-180 transition-transform" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={8}
              className="w-[400px] p-4 rounded-lg border border-gray-200 shadow-lg bg-white"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <div className="grid grid-cols-3 gap-3">
                {spaceTools.map((tool) => {
                  const Icon = tool.icon;
                  const isActive = tool.tool_type === 'matrix';

                  if (!Icon) return null;

                  const isValidComponent = typeof Icon === 'function' || (typeof Icon === 'object' && Icon.$$typeof);
                  if (!isValidComponent) return null;

                  return (
                    <div
                      key={tool.id}
                      className={`relative flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      onClick={(e) => {
                        if (tool.tool_type === 'matrix') return;

                        const toolRoutes = {
                          team: `/space/${projectId}/team`,
                          compliance: `/space/${projectId}/compliance`,
                          bookings: `/space/${projectId}/bookings`,
                          qualifications: `/space/${projectId}/qualifications`,
                          chat: `/chat?space=${projectId}`,
                          message_board: `/space/${projectId}/message-board`,
                          check_ins: `/space/${projectId}/checkins`,
                          checkins: `/space/${projectId}/checkins`,
                          calendar: `/space/${projectId}/schedule`,
                          checklists: `/space/${projectId}/checklists`,
                          docs: `/space/${projectId}/docs`,
                        };

                        const route = toolRoutes[tool.tool_type];
                        if (route) {
                          if (e.ctrlKey || e.metaKey) {
                            window.open(route, '_blank');
                          } else {
                            navigate(route);
                          }
                        } else {
                          if (e.ctrlKey || e.metaKey) {
                            window.open(`/space/${projectId}`, '_blank');
                          } else {
                            navigate(`/space/${projectId}`);
                          }
                        }
                      }}
                    >
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 flex-shrink-0"
                        style={{ backgroundColor: tool.color + '20' }}
                      >
                        <Icon className="h-6 w-6" style={{ color: tool.color }} />
                      </div>
                      <div className="h-8 flex items-center justify-center">
                        <span className="text-xs font-medium text-center line-clamp-2 w-full leading-tight">{tool.name}</span>
                      </div>
                    </div>
                  );
                })}
                {spaceTools.length === 0 && (
                  <div className="col-span-3 text-sm text-gray-500 text-center py-4">No tools configured</div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Breadcrumb with editable Matrix name */}
          <EditableMatrixName
            initialName={matrixName}
            spaceName={spaceName}
            projectId={projectId}
            onNameChange={setMatrixName}
            isStackView={isStackView}
            stackId={stackId}
            stackName={stackName}
          />
        </div>

        {/* Right side - Import, Search, Sort & View Toggle */}
        <div className="flex items-center gap-3">
          {/* Import Button - Only show for single space view */}
          {!isStackView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/space/${projectId}/import-matrix`)}
              className="h-8 gap-1.5 text-xs"
            >
              <FileUp className="h-3.5 w-3.5" />
              Import
            </Button>
          )}

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search employees & qualifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-80 h-8 pl-9 pr-4 text-sm bg-white"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Sort Dropdown - Only show in qualifications/employees view */}
          {(viewMode === 'qualifications' || viewMode === 'employees') && (
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px] h-8">
                <div className="flex items-center gap-2">
                  <ArrowUpDown className="h-3.5 w-3.5" />
                  <span className="text-sm">
                    {sortBy === 'name-asc' && 'Name (A-Z)'}
                    {sortBy === 'name-desc' && 'Name (Z-A)'}
                    {sortBy === 'compliance-asc' && 'Compliance ↑'}
                    {sortBy === 'compliance-desc' && 'Compliance ↓'}
                    {sortBy === 'gap-desc' && 'Gap (Most)'}
                    {sortBy === 'gap-asc' && 'Gap (Least)'}
                    {sortBy === 'missing-desc' && 'Missing (Most)'}
                    {sortBy === 'missing-asc' && 'Missing (Least)'}
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">
                  <div className="flex items-center gap-2">
                    <ArrowUp className="h-3 w-3" />
                    Name (A-Z)
                  </div>
                </SelectItem>
                <SelectItem value="name-desc">
                  <div className="flex items-center gap-2">
                    <ArrowDown className="h-3 w-3" />
                    Name (Z-A)
                  </div>
                </SelectItem>
                <SelectItem value="compliance-asc">
                  <div className="flex items-center gap-2">
                    <ArrowUp className="h-3 w-3" />
                    Compliance (Low-High)
                  </div>
                </SelectItem>
                <SelectItem value="compliance-desc">
                  <div className="flex items-center gap-2">
                    <ArrowDown className="h-3 w-3" />
                    Compliance (High-Low)
                  </div>
                </SelectItem>
                <SelectItem value="gap-asc">
                  <div className="flex items-center gap-2">
                    <ArrowUp className="h-3 w-3" />
                    Gap (Least first)
                  </div>
                </SelectItem>
                <SelectItem value="gap-desc">
                  <div className="flex items-center gap-2">
                    <ArrowDown className="h-3 w-3" />
                    Gap (Most first)
                  </div>
                </SelectItem>
                <SelectItem value="missing-asc">
                  <div className="flex items-center gap-2">
                    <ArrowUp className="h-3 w-3" />
                    Missing (Least first)
                  </div>
                </SelectItem>
                <SelectItem value="missing-desc">
                  <div className="flex items-center gap-2">
                    <ArrowDown className="h-3 w-3" />
                    Missing (Most first)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* View Toggle - Animated sliding background */}
          <div className="relative inline-flex items-center p-1 bg-gray-100 rounded-lg">
            {/* Sliding background indicator */}
            <motion.div
              className="absolute h-[32px] bg-white shadow-sm rounded-md pointer-events-none"
              initial={false}
              animate={{
                x: viewMode === 'grid' ? 0 :
                   viewMode === 'qualifications' ? 84 :
                   viewMode === 'employees' ? 220 :
                   330,
                width: viewMode === 'grid' ? 84 :
                       viewMode === 'qualifications' ? 136 :
                       viewMode === 'employees' ? 110 :
                       70
              }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 30,
                mass: 0.8
              }}
              style={{ left: '4px' }}
            />

            {/* Matrix button */}
            <button
              className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                viewMode === 'grid' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setViewMode('grid')}
              style={{ width: '84px' }}
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              <span className="text-xs font-medium">Matrix</span>
            </button>

            {/* Qualifications button */}
            <button
              className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                viewMode === 'qualifications' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setViewMode('qualifications')}
              style={{ width: '136px' }}
            >
              <Award className="h-4 w-4 mr-2" />
              <span className="text-xs font-medium">Qualifications</span>
            </button>

            {/* Employees button */}
            <button
              className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                viewMode === 'employees' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setViewMode('employees')}
              style={{ width: '110px' }}
            >
              <Users className="h-4 w-4 mr-2" />
              <span className="text-xs font-medium">Employees</span>
            </button>

            {/* Site button */}
            <button
              className={`relative flex items-center justify-center px-3 py-1.5 transition-colors duration-200 flex-shrink-0 ${
                viewMode === 'site' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setViewMode('site')}
              style={{ width: '70px' }}
            >
              <MapPin className="h-4 w-4 mr-2" />
              <span className="text-xs font-medium">Site</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

MatrixToolbar.displayName = 'MatrixToolbar';

export default MatrixToolbar;
